<template>
  <funding-assessments-base :state="state">
    <template #headerAction>
      <div class="md:flex items-baseline">
        <div class="flex-1">
          <label for="lock_grading" class="sr-only">
            Lock assessment phase?
          </label>
        </div>
        <div class="mt-6 md:ml-4 md:mt-0">
          <input
            id="lock_grading"
            v-model="lockGrading"
            type="checkbox"
            class="hidden"
            name="lock_grading"
            :disabled="loading"
          />
          <div
            class="border flex uppercase font-bold overflow-hidden cursor-pointer text-center text-sm"
          >
            <div
              class="w-full py-2 px-6 m-1 outline-none"
              tabindex="0"
              :class="{
                'bg-negative-600 text-white': lockGrading && !loading,
                'hover:bg-negative-200 focus:bg-negative-200 hover:text-negative-600':
                  !lockGrading && !loading,
                'bg-grey-600 text-white': loading && lockGrading
              }"
              @click.self="setLockGrading(true)"
              @keydown.enter="setLockGrading(true)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-6 h-6"
              >
                <path
                  d="M18 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v4h-3v14h18v-14h-3zm-10 0v-4c0-2.206 1.794-4 4-4s4 1.794 4 4v4h-8z"
                />
              </svg>
              <span class="sr-only">Lock</span>
            </div>
            <div
              class="w-full py-2 px-8 m-1 outline-none"
              tabindex="0"
              :class="{
                'bg-positive-600 text-white': !lockGrading && !loading,
                'hover:bg-positive-200 focus:bg-positive-200 hover:text-positive-600':
                  lockGrading && !loading,
                'bg-grey-600 text-white': loading && !lockGrading
              }"
              @click.self="setLockGrading(false)"
              @keydown.enter="setLockGrading(false)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                class="w-6 h-6"
              >
                <path
                  d="M12 10v-4c0-3.313-2.687-6-6-6s-6 2.687-6 6v3h2v-3c0-2.206 1.794-4 4-4s4 1.794 4 4v4h-4v14h18v-14h-12z"
                />
              </svg>
              <span class="sr-only">Unlock</span>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div
      v-if="!isSingleStream"
      class="border-b border-grey-300"
    >
      <nav class="navigation container py-2">
        <ul class="flex items-center justify-center">
          <li class="navigation-item mr-2 text-xs font-bold">
            Filter:
          </li>
          <li
            v-for="stream in $page.stream_counts"
            :key="stream.key"
            class="navigation-item"
          >
            <label
              class="no-underline cursor-pointer font-semibold text-sm block border-2 rounded-full py-1 px-4 transition-all transition-fast"
              :style="isActive(stream.key) ? {...getStreamColorsByKey(stream.key)} : {}"
            >
              <input
                v-model="selectedStream"
                name="streams"
                type="checkbox"
                :value="stream.key || 'unknown'"
                class="sr-only"
                :disabled="selectedStream.length == 1 && isActive(stream.key)"
              />
              {{ stream.name }} ({{ stream.count }})
            </label>
          </li>
        </ul>
      </nav>
    </div>
    <div class="container pt-12">
      <template v-if="state.applications.length">
        <league-table
          :streams="selectedStream"
          :applications="state.applications"
          :break-position="breakPosition"
        />
        <div class="mt-8 flex items-center">
          <div class="w-64">
            <app-button
              tag="a"
              :href="
                route('funding.assessments.review', [
                  state.funding_pot.id,
                  state.marking_phase.id
                ])
              "
            >
              Review & Confirm
            </app-button>
          </div>
          <div class="ml-6 w-64">
            <app-button
              tag="a"
              colour="secondary"
              data-turbolinks="false"
              :href="
                route('funding.assessments.phase.export', [
                  state.funding_pot.id,
                  state.marking_phase.id
                ])
              "
            >
              Export
            </app-button>
          </div>
          <div class="ml-auto">
            <label for="break" class="font-bold mr-2">Break Position:</label>
            <input
              id="break"
              v-model.number="breakPosition"
              type="number"
              min="0"
              :max="state.applications.length - 1"
              class="border-2 p-2 focus:outline-none focus:border-primary-500 transition-all transition-normal"
            />
          </div>
        </div>
      </template>
      <template v-else>
        <div class="border-4 border-dashed p-12 text-center max-w-md mx-auto">
          <h1>No assessments available</h1>
          <p>The league table will appear when assessments become available.</p>
          <div class="w-48 mx-auto mt-6">
            <app-button
              tag="a"
              :href="
                route('funding.assessments.assessors', [
                  state.funding_pot.id,
                  state.marking_phase.id
                ])
              "
            >
              Assign Assessors
            </app-button>
          </div>
        </div>
      </template>
    </div>
  </funding-assessments-base>
</template>

<script>
import { competitionStreamsMixin } from "../mixins/competitionStreams";

export default {
  name: "FundingAssessmentsGrading",
  mixins: [competitionStreamsMixin],
  data() {
    return {
      loading: false,
      lockGrading: false,
      breakPosition: "",
      selectedStream: []
    };
  },
  computed: {
    funding_pot() {
      return this.state.funding_pot;
    },
    storeKey() {
      return `break::${window.location.pathname}`;
    }
  },
  watch: {
    lockGrading(newValue) {
      if (this.loading) return;
      this.loading = true;
      axios
        .post(
          route("funding.assessments.phase.locking", [
            this.state.funding_pot.id,
            this.state.marking_phase.id
          ]),
          {
            lock: newValue
          }
        )
        .then(() => {})
        .catch(() => (this.lockGrading = !newValue))
        .then(() => (this.loading = false));
    },
    breakPosition(value) {
      if (value) {
        window.localStorage.setItem(this.storeKey, value);
      } else {
        window.localStorage.removeItem(this.storeKey);
      }
    }
  },
  created() {
    const savedBreakPosition = window.localStorage.getItem(this.storeKey);
    this.lockGrading = this.state.marking_phase.locked || false;
    this.assessments = this.state.assessments;
    (this.breakPosition = savedBreakPosition
      ? parseInt(savedBreakPosition)
      : ""),
      (document.title = `${this.state.marking_phase.name} Grading :: ${document.title}`);
  },
  methods: {
    isActive(stream) {
      return this.selectedStream.includes(stream);
    },
    setLockGrading(value) {
      if (this.loading) return;
      this.lockGrading = value;
    }
  },
  mounted() {
    this.selectedStream = this.$page.competition_streams?.map(s => s.key) ?? []
  }
};
</script>
